/**
 * Mayvenn — cart drawer cross-sell ("Complete your install"), 10 Sept 2026.
 *
 * One-tap add for the cards rendered by snippets/mv-cart-upsell.liquid. Uses event delegation on
 * document, so it keeps working after Horizon morphs the drawer on every cart change.
 *
 * Adds through Horizon's own contract: dispatch a CartLinesUpdateEvent with a deferred promise,
 * POST /cart/add.js asking for the cart sections, then resolve the promise with the fresh cart and
 * section HTML. <cart-items-component> morphs the drawer, the header bubble updates, and analytics
 * listeners on the standard event see a normal add — same path as the product form.
 *
 * 11 Sept 2026: the blog product cards (snippets/mv-article-product-card.liquid) reuse this contract.
 * A card with data-mv-source="blog" reports as a blog add (source + dataLayer event carry the
 * article handle), and because that card is not inside the drawer, its button shows "Added" and
 * resets instead of waiting for the drawer morph to remove it. The drawer opens itself
 * (cart-drawer-component has auto-open and opens on an "add" lines update).
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADD = '[data-mv-upsell-add]';
const CARD = '[data-mv-upsell]';

/** @param {Element} card */
function selectedVariant(card) {
  const select = /** @type {HTMLSelectElement | null} */ (card.querySelector('[data-mv-upsell-variant]'));
  const button = /** @type {HTMLButtonElement | null} */ (card.querySelector(ADD));
  return select?.value || button?.dataset.variantId || '';
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  const res = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return res.json();
}

/** @param {HTMLButtonElement} button */
async function add(button) {
  const card = button.closest(CARD);
  if (!card || button.getAttribute('aria-busy') === 'true') return;

  const variantId = selectedVariant(card);
  if (!variantId) return;

  const error = /** @type {HTMLElement | null} */ (card.querySelector('.mv-cu__error'));
  if (error) error.hidden = true;

  const label = button.textContent;
  const source = card.getAttribute('data-mv-source') === 'blog' ? 'mv-blog' : 'mv-cart-upsell';
  const inDrawer = !!button.closest('cart-drawer-component');
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'Adding…';

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: cartSectionIds().join(','),
      }),
    });
    const data = await res.json();

    if (data.status) {
      // Shopify error (e.g. sold out since the drawer rendered)
      if (error) {
        error.textContent = data.description || data.message || 'That option just sold out.';
        error.hidden = false;
      }
      button.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source, itemCount: 0 },
      });
      button.removeAttribute('aria-busy');
      button.textContent = label;
      return;
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source,
        itemCount: 1,
        sections: data.sections,
        didError: false,
      },
    });

    if (source === 'mv-blog') {
      window.dataLayer?.push({ event: 'mv_blog_add_to_cart', variant_id: variantId, mv_article: card.getAttribute('data-mv-article') || '' });
    } else {
      window.dataLayer?.push({ event: 'mv_cart_upsell_add', variant_id: variantId });
    }

    if (inDrawer) return; // The drawer morphs and this card disappears (the product is now in the cart).

    // Outside the drawer (blog cards) the card stays on the page, so confirm and reset.
    button.textContent = 'Added';
    button.setAttribute('data-mv-added', '');
    setTimeout(() => {
      button.removeAttribute('aria-busy');
      button.removeAttribute('data-mv-added');
      button.textContent = label;
    }, 2200);
  } catch (err) {
    console.error('[mv-cart] add failed', err);
    deferred.reject(err);
    if (error) {
      error.textContent = 'Could not add right now. Please try again.';
      error.hidden = false;
    }
    button.removeAttribute('aria-busy');
    button.textContent = label;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest(ADD) : null;
  if (!(target instanceof HTMLButtonElement)) return;
  event.preventDefault();
  add(target);
});

// Keep the price and button in step with the dropdown.
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-mv-upsell-variant]')) return;
  const card = select.closest(CARD);
  if (!card) return;
  const option = select.selectedOptions[0];
  const price = card.querySelector('[data-mv-upsell-price]');
  if (price && option?.dataset.price) price.textContent = option.dataset.price;
  const button = /** @type {HTMLButtonElement | null} */ (card.querySelector(ADD));
  if (button) button.dataset.variantId = select.value;
});
